import { Box, Flex, Text } from '@chakra-ui/react';
import { AdminPageWrapper } from 'components/templates/Admin';
import { AuthContext } from 'modules/shared/context/AuthContext';
import { getShouldEnforcePermissions, getUsersPermissions } from 'modules/shared/hooks/usePermissions';
import { useRouter } from 'next/router';
import { useContext } from 'react';
import routes from 'routes';
import * as permissions from '../constants/permissions';

const pagePermissions = {
  [routes.AppointmentList.to]: [permissions.APPOINTMENTS_READ, permissions.APPOINTMENTS_OFFSHORE_READ],
  [routes.AppointmentDetails.to]: [permissions.APPOINTMENT_READ, permissions.APPOINTMENT_OFFSHORE_READ],
  [routes.AssessmentList.to]: [
    permissions.ASSESSMENTS_READ,
    permissions.ASSESSMENTS_OFFSHORE_READ,
    permissions.ASSESSMENTS_TRIAL_READ,
  ],
  [routes.AssessmentDetails.to]: [
    permissions.ASSESSMENT_READ,
    permissions.ASSESSMENT_OFFSHORE_READ,
    permissions.ASSESSMENT_TRIAL_READ,
  ],
  [routes.CaregiversList.to]: [permissions.CARE_PROVIDERS_READ],
  [routes.CaregiverDetails.to]: [permissions.CARE_PROVIDER_READ],
  [routes.CensusIngestionList.to]: [permissions.CENSUS_INGESTIONS_READ, permissions.CENSUS_INGESTIONS_OFFSHORE_READ],
  [routes.CensusIngestionDetails.to]: [permissions.CENSUS_INGESTION_READ, permissions.CENSUS_INGESTION_OFFSHORE_READ],
  [routes.CensusIngestionCreate.to]: [permissions.CENSUS_INGESTION_CREATE],
  [routes.ChannelPartnerList.to]: [permissions.CHANNEL_PARTNERS_READ, permissions.CHANNEL_PARTNERS_OFFSHORE_READ],
  [routes.ChannelPartnerDetails.to]: [permissions.CHANNEL_PARTNER_READ, permissions.CHANNEL_PARTNER_OFFSHORE_READ],
  [routes.ConnectedBenefitList.to]: [permissions.CONNECTED_BENEFITS_READ],
  [routes.CoveredLives.to]: [permissions.COVERED_LIVES_READ, permissions.COVERED_LIVES_OFFSHORE_READ],
  [routes.CoveredLife.to]: [permissions.COVERED_LIFE_READ, permissions.COVERED_LIFE_OFFSHORE_READ],
  [routes.CustomerList.to]: [permissions.CUSTOMERS_READ, permissions.CUSTOMERS_OFFSHORE_READ],
  [routes.CustomerDetails.to]: [permissions.CUSTOMER_READ, permissions.CUSTOMER_OFFSHORE_READ],
  [routes.DeactivationsList.to]: [
    permissions.CUSTOMER_DEACTIVATIONS_READ,
    permissions.CUSTOMER_DEACTIVATIONS_OFFSHORE_READ,
  ],
  [routes.DeactivationDetail.to]: [
    permissions.CUSTOMER_DEACTIVATION_READ,
    permissions.CUSTOMER_DEACTIVATION_OFFSHORE_READ,
  ],
  [routes.DemoAccountList.to]: [permissions.DEMO_ACCOUNTS_READ],
  [routes.DemoAccountCreate.to]: [permissions.DEMO_ACCOUNT_CREATE],
  [routes.MemberList.to]: [permissions.MEMBERS_READ, permissions.MEMBERS_OFFSHORE_READ],
  [routes.MemberDetails.to]: [permissions.MEMBER_READ, permissions.MEMBER_OFFSHORE_READ],
  [routes.MarketplaceEnrollmentDetails.to]: [permissions.MARKETPLACE_ENROLLMENT_READ],
  [routes.TrialList.to]: [permissions.TRIALS_READ, permissions.TRIALS_OFFSHORE_READ],
  [routes.TrialDetails.to]: [permissions.TRIAL_READ, permissions.TRIAL_OFFSHORE_READ],
  [routes.TrialCreate.to]: [permissions.TRIAL_CREATE],
  [routes.UserList.to]: [permissions.USERS_READ, permissions.USERS_OFFSHORE_READ],
  [routes.UserDetails.to]: [permissions.USER_READ, permissions.USER_OFFSHORE_READ],
  [routes.BCBSPrefixesList.to]: [permissions.BLUE_CROSS_MEMBER_ID_PREFIXES_READ],
  [routes.BulkMemberChargesList.to]: [permissions.BULK_CHARGE_RUNS_READ],
  [routes.BulkMemberChargesDetails.to]: [permissions.BULK_CHARGE_RUN_READ],
  [routes.BulkMemberChargesCreate.to]: [permissions.BULK_CHARGE_RUN_CREATE],
  [routes.Regions.to]: [permissions.ADMIN_PORTAL_REGIONS_FILES_READ],
  [routes.RegionDetails.to]: [permissions.ADMIN_PORTAL_REGIONS_FILES_READ],
};

export const PageAccess = ({ children }) => {
  const { user } = useContext(AuthContext);
  const router = useRouter();

  const hasAccessToThisPage = () => {
    const permissionsNeededToGiveAccess = pagePermissions[router.route] || [];

    if (permissionsNeededToGiveAccess.length === 0) return true;

    const usersPermissions = getUsersPermissions(user?.scopes || []);

    const shouldEnforcePermissions = getShouldEnforcePermissions(user?.scopes || []);

    if (!shouldEnforcePermissions) return true;

    if (usersPermissions.length === 0) return true;

    return permissionsNeededToGiveAccess?.some((permission) => {
      return usersPermissions.includes(permission);
    });
  };

  if (hasAccessToThisPage() === false) {
    return (
      <AdminPageWrapper>
        <Flex justifyContent="center" alignItems="center" width="100%">
          <Box mt="8">
            <Text fontSize="2xl" fontWeight="bold" mb={4}>
              You don&apos;t have permission to view this page
            </Text>
            <Text>Please contact your administrator if you believe this is an error.</Text>
          </Box>
        </Flex>
      </AdminPageWrapper>
    );
  }

  return <>{children}</>;
};
